import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Tags, Trash2, Save, Plus, Eye } from "lucide-react";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  addTags,
  listTags,
  listSegments,
  saveSegment,
  deleteSegment,
  previewSegment,
} from "@/lib/segments.functions";

export const Route = createFileRoute("/_authenticated/segments")({
  component: SegmentsPage,
  head: () => ({ meta: [{ title: "Empfänger-Segmente — InboxPulse" }] }),
});

type Filter = {
  tags_any?: string[];
  not_opened_days?: number;
  exclude_bounced?: boolean;
  exclude_suppressed?: boolean;
  email_contains?: string;
};

function SegmentsPage() {
  const qc = useQueryClient();
  const addTagsFn = useServerFn(addTags);
  const listTagsFn = useServerFn(listTags);
  const listSegFn = useServerFn(listSegments);
  const saveSegFn = useServerFn(saveSegment);
  const deleteSegFn = useServerFn(deleteSegment);
  const previewFn = useServerFn(previewSegment);

  const tagsQ = useQuery({ queryKey: ["rec-tags"], queryFn: () => listTagsFn() });
  const segsQ = useQuery({ queryKey: ["rec-segments"], queryFn: () => listSegFn() });

  /* Tag-Editor */
  const [emailsText, setEmailsText] = useState("");
  const [tagsText, setTagsText] = useState("");
  const mAddTags = useMutation({
    mutationFn: () => {
      const emails = Array.from(
        new Set(
          emailsText
            .split(/[\s,;\n\r\t]+/)
            .map((s) => s.trim().toLowerCase())
            .filter((s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s)),
        ),
      );
      const tagList = Array.from(
        new Set(
          tagsText
            .split(/[,\s]+/)
            .map((s) => s.trim())
            .filter(Boolean),
        ),
      );
      if (!emails.length) throw new Error("Keine gültigen E-Mails.");
      if (!tagList.length) throw new Error("Keinen Tag angegeben.");
      return addTagsFn({ data: { emails, tags: tagList } });
    },
    onSuccess: (r) => {
      toast.success(`${r.added} Tag-Zuordnungen gespeichert.`);
      setEmailsText("");
      setTagsText("");
      qc.invalidateQueries({ queryKey: ["rec-tags"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  /* Segment-Builder */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [filter, setFilter] = useState<Filter>({
    exclude_bounced: true,
    exclude_suppressed: true,
  });
  const [previewData, setPreviewData] = useState<{ count: number; sample: string[] } | null>(null);

  function resetForm() {
    setEditingId(null);
    setName("");
    setDescription("");
    setFilter({ exclude_bounced: true, exclude_suppressed: true });
    setPreviewData(null);
  }

  const mSave = useMutation({
    mutationFn: () =>
      saveSegFn({
        data: {
          id: editingId ?? undefined,
          name,
          description: description || null,
          filter,
        },
      }),
    onSuccess: () => {
      toast.success("Segment gespeichert.");
      resetForm();
      qc.invalidateQueries({ queryKey: ["rec-segments"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const mDelete = useMutation({
    mutationFn: (id: string) => deleteSegFn({ data: { id } }),
    onSuccess: () => {
      toast.success("Segment gelöscht.");
      qc.invalidateQueries({ queryKey: ["rec-segments"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const mPreview = useMutation({
    mutationFn: () => previewFn({ data: { filter } }),
    onSuccess: (r) => setPreviewData(r),
    onError: (e: Error) => toast.error(e.message),
  });

  const tagOptions = useMemo(() => tagsQ.data?.tags ?? [], [tagsQ.data]);

  return (
    <div className="p-8 max-w-6xl space-y-8">
      <PageHeader
        title="Empfänger-Segmente"
        description="Tagge Empfänger und definiere Filter wie 'nicht geöffnet in 30 Tagen' für zielgenauen Versand."
      />

      <HelpPanel
        title="Hilfe: Empfänger gruppieren (Segmente)"
        what={
          <>
            Ein <b>Segment</b> ist eine schlaue Unter-Liste deiner Empfänger, z.B. "alle die in den
            letzten 30 Tagen nicht geöffnet haben" oder "alle mit Tag Premium-Kunde".
          </>
        }
        why={
          <>
            Statt immer an alle zu schicken, sprichst du nur die richtigen Leute an. Das erhöht
            Öffnungs-Raten massiv und reduziert Spam-Beschwerden.
          </>
        }
        how={[
          <>
            <b>Empfänger taggen</b>: in der Empfänger-Tabelle Tags wie "kunde", "lead", "kalt"
            vergeben.
          </>,
          <>
            <b>Neues Segment</b> anlegen — Filter wählen (Tags, Verhalten, Datum).
          </>,
          <>
            <b>Speichern</b> — das Segment ist im Kampagnen-Wizard als Ziel-Liste auswählbar.
          </>,
        ]}
        tips={[
          <>
            Inaktive (60+ Tage nicht geöffnet) → eigenes Segment, dann Reaktivierungs-Mail oder
            löschen.
          </>,
          <>
            Wer 3x in Folge nicht geöffnet hat, sollte pausiert werden — sonst leidet deine
            Reputation.
          </>,
        ]}
        glossary={[
          {
            term: "Tag",
            def: "Etikett, das du Empfängern gibst (z.B. VIP, Newsletter). Wie Post-its auf Kontakten.",
          },
          {
            term: "Segment",
            def: "Gespeicherter Filter, der dir automatisch alle passenden Empfänger zeigt.",
          },
        ]}
      />

      {/* ---- Tagger ---- */}
      <Card className="p-5 space-y-3">
        <h2 className="font-semibold flex items-center gap-2">
          <Tags className="size-4" /> Empfänger mit Tags versehen
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">E-Mails (CSV oder eine pro Zeile)</Label>
            <Textarea
              rows={5}
              value={emailsText}
              onChange={(e) => setEmailsText(e.target.value)}
              placeholder="alice@example.com&#10;bob@example.com"
            />
          </div>
          <div>
            <Label className="text-xs">Tags (Komma- oder Leerzeichen-getrennt)</Label>
            <Input
              value={tagsText}
              onChange={(e) => setTagsText(e.target.value)}
              placeholder="vip newsletter promo"
            />
            <div className="mt-2 text-xs text-muted-foreground">
              Erlaubt: a-z, A-Z, 0-9, Leerzeichen, _ und -.
            </div>
          </div>
        </div>
        <div>
          <Button onClick={() => mAddTags.mutate()} disabled={mAddTags.isPending}>
            <Plus className="size-4 mr-1" /> Tags speichern
          </Button>
        </div>
        {tagOptions.length > 0 && (
          <div className="pt-2 flex flex-wrap gap-1.5 text-xs">
            {tagOptions.map((t) => (
              <Badge key={t.tag} variant="secondary">
                {t.tag} · {t.count}
              </Badge>
            ))}
          </div>
        )}
      </Card>

      {/* ---- Segment-Builder ---- */}
      <Card className="p-5 space-y-4">
        <h2 className="font-semibold">{editingId ? "Segment bearbeiten" : "Neues Segment"}</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Beschreibung (optional)</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>

        <div>
          <Label className="text-xs">Tags (mind. einer trifft zu)</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {tagOptions.length === 0 && (
              <span className="text-xs text-muted-foreground">
                Noch keine Tags vorhanden – oben Empfänger taggen.
              </span>
            )}
            {tagOptions.map((t) => {
              const active = filter.tags_any?.includes(t.tag) ?? false;
              return (
                <button
                  key={t.tag}
                  type="button"
                  onClick={() => {
                    const set = new Set(filter.tags_any ?? []);
                    if (active) set.delete(t.tag);
                    else set.add(t.tag);
                    setFilter({ ...filter, tags_any: Array.from(set) });
                  }}
                  className={`text-xs px-2 py-1 rounded border transition ${
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card text-muted-foreground border-border hover:text-foreground"
                  }`}
                >
                  {t.tag}
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Nicht geöffnet in (Tagen)</Label>
            <Input
              type="number"
              min={1}
              value={filter.not_opened_days ?? ""}
              onChange={(e) =>
                setFilter({
                  ...filter,
                  not_opened_days: e.target.value ? Number(e.target.value) : undefined,
                })
              }
              placeholder="z.B. 30"
            />
          </div>
          <div>
            <Label className="text-xs">E-Mail enthält</Label>
            <Input
              value={filter.email_contains ?? ""}
              onChange={(e) =>
                setFilter({ ...filter, email_contains: e.target.value || undefined })
              }
              placeholder="z.B. @gmail.com"
            />
          </div>
          <div className="flex flex-col gap-2 justify-end">
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={!!filter.exclude_bounced}
                onCheckedChange={(v) => setFilter({ ...filter, exclude_bounced: v })}
              />
              Bouncer ausschließen
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch
                checked={!!filter.exclude_suppressed}
                onCheckedChange={(v) => setFilter({ ...filter, exclude_suppressed: v })}
              />
              Gesperrte ausschließen
            </label>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => mPreview.mutate()} disabled={mPreview.isPending}>
            <Eye className="size-4 mr-1" /> Vorschau
          </Button>
          <Button onClick={() => mSave.mutate()} disabled={!name || mSave.isPending}>
            <Save className="size-4 mr-1" /> {editingId ? "Aktualisieren" : "Segment speichern"}
          </Button>
          {editingId && (
            <Button variant="ghost" onClick={resetForm}>
              Abbrechen
            </Button>
          )}
        </div>

        {previewData && (
          <div className="text-sm">
            <strong>{previewData.count}</strong> Empfänger gefunden.
            {previewData.sample.length > 0 && (
              <div className="mt-2 text-xs text-muted-foreground font-mono break-all">
                {previewData.sample.slice(0, 20).join(", ")}
                {previewData.sample.length > 20 && " …"}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* ---- Gespeicherte Segmente ---- */}
      <Card className="p-5">
        <h2 className="font-semibold mb-3">Gespeicherte Segmente</h2>
        {!segsQ.data?.segments.length ? (
          <div className="text-sm text-muted-foreground">Noch keine Segmente.</div>
        ) : (
          <div className="divide-y divide-border">
            {segsQ.data.segments.map((s) => (
              <div key={s.id} className="py-3 flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium">{s.name}</div>
                  {s.description && (
                    <div className="text-xs text-muted-foreground">{s.description}</div>
                  )}
                  <div className="text-xs text-muted-foreground mt-1 font-mono">
                    {JSON.stringify(s.filter_json)}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingId(s.id);
                      setName(s.name);
                      setDescription(s.description ?? "");
                      setFilter(s.filter_json as Filter);
                      setPreviewData(null);
                    }}
                  >
                    Bearbeiten
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (confirm(`Segment "${s.name}" löschen?`)) mDelete.mutate(s.id);
                    }}
                  >
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
